/**
 * MarketBreadth.jsx
 * ──────────────────
 * Main page — mirrors nifty_breadth_lightweight_chart.html layout exactly:
 *   • Header  : title + stats cards (Range, Observations, Latest Close, each %)
 *   • Toolbar : Timeframe | Range | Navigate | Zoom | Display | Breadth
 *   • Chart   : BreadthChart (lightweight-charts, multi-pane)
 *   • Legend  : color swatches
 *
 * Extra vs HTML: top index tabs (Nifty 50 / Nifty 500) unique to this React app.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Sun, Moon } from "lucide-react";
import DashboardLayout from "../DashboardLayout";
import BreadthChart, { INDICATORS } from "./BreadthChart";
import "./MarketBreadth.css";
import apiClient from "../../utils/apiClient";

const INDEXES = [
  { key: "nifty50",  label: "Nifty 50"  },
  { key: "nifty500", label: "Nifty 500" },
];

const RANGES = ["6M", "YTD", "1Y", "3Y", "5Y", "10Y", "ALL"];

export default function MarketBreadth() {
  // ── State ─────────────────────────────────────────────────────────────────
  const [theme,         setTheme]         = useState("dark");
  const [activeIndex,   setActiveIndex]   = useState("nifty50");
  const [timeframe,     setTimeframe]     = useState("daily");
  const [range,         setRange]         = useState("ALL");
  const [barSpacing,    setBarSpacing]    = useState(7);
  const [showCrosshair, setShowCrosshair] = useState(true);
  const [showGrid,      setShowGrid]      = useState(true);
  const [showPaneLabels,setShowPaneLabels]= useState(true);
  const [compact,       setCompact]       = useState(false);
  const [visibleSeries, setVisibleSeries] = useState(
    () => new Set(INDICATORS.map(i => i.key))
  );

  const [breadthData, setBreadthData]  = useState(null);
  const [latestSnap,  setLatestSnap]   = useState(null);
  const [loading,     setLoading]      = useState(false);
  const [error,       setError]        = useState(null);

  // Chart action handle (set by BreadthChart via onChartReady)
  const chartActionsRef = useRef(null);

  // ── Fetch data ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dataRes, latestRes] = await Promise.all([
        apiClient.get(`breadth/data?index=${activeIndex}&timeframe=${timeframe}`),
        apiClient.get(`breadth/latest`),  // returns all indices at once, no params
      ]);
      setBreadthData(dataRes.data);
      setLatestSnap(latestRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load breadth data");
    } finally {
      setLoading(false);
    }
  }, [activeIndex, timeframe]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Toolbar handlers ───────────────────────────────────────────────────────
  const handleRange = (r) => {
    setRange(r);
    chartActionsRef.current?.setRange(r);
  };

  const handleBarSpacing = (v) => {
    const n = Number(v);
    setBarSpacing(n);
    chartActionsRef.current?.setBarSpacing(n);
  };

  const handleToggleSeries = (key) => {
    setVisibleSeries(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleShowAll = () => setVisibleSeries(new Set(INDICATORS.map(i => i.key)));
  const handleHideAll = () => setVisibleSeries(new Set());

  const handleGrid = () => {
    const next = !showGrid;
    setShowGrid(next);
    chartActionsRef.current?.toggleGrid(next);
  };

  const handleCrosshair = () => {
    const next = !showCrosshair;
    setShowCrosshair(next);
    chartActionsRef.current?.toggleCrosshair(next);
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const stats = (() => {
    if (!breadthData) return null;
    const dates   = breadthData.dates  ?? [];
    const n       = dates.length;
    const minDate = dates[0]  ?? "—";
    const maxDate = dates[n - 1] ?? "—";
    const lastClose = breadthData.ohlc?.[n - 1]?.c;
    // latestSnap is keyed by "{index}_{timeframe}" e.g. "nifty50_daily"
    const snap = latestSnap?.[`${activeIndex}_${timeframe}`];
    return {
      range:     `${minDate} to ${maxDate}`,
      obs:       n.toLocaleString(),
      close:     lastClose != null ? Number(lastClose).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—",
      dma200:    fmt(snap?.breadth_200dma_pct),
      dma50:     fmt(snap?.breadth_50dma_pct),
      dma10:     fmt(snap?.breadth_10dma_pct),
      blue:      fmt(snap?.breadth_blue_bar_pct),
      red:       fmt(snap?.breadth_red_bar_pct),
      hhpc:      fmt(snap?.breadth_hhpc_pct),
      llpc:      fmt(snap?.breadth_llpc_pct),
    };
  })();

  const indexLabel = INDEXES.find(i => i.key === activeIndex)?.label ?? activeIndex;

  return (
    <DashboardLayout title="Market Breadth">
      <div className={`mb-page ${theme}`}>
      {/* ── Index Tabs (React-specific addition) ── */}
      <div className="mb-index-tabs">
        {INDEXES.map(idx => (
          <button
            key={idx.key}
            className={`mb-index-tab${activeIndex === idx.key ? " active" : ""}`}
            onClick={() => setActiveIndex(idx.key)}
          >
            {idx.label}
          </button>
        ))}
      </div>

      {/* ── Header ── */}
      <div className="mb-header">
        <div>
          <h1 className="mb-h1">{indexLabel} Price vs Breadth</h1>
          <p className="mb-desc">
            Daily or weekly {indexLabel} candlesticks with dynamically removable breadth panes.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="mb-stats">
          <StatCard label="Range"        value={stats?.range   ?? "—"} />
          <StatCard label="Observations" value={stats?.obs     ?? "—"} />
          <StatCard label="Latest Close" value={stats?.close   ?? "—"} />
          <StatCard label="200DMA"       value={stats?.dma200  ?? "—"} color="#2563eb" />
          <StatCard label="50DMA"        value={stats?.dma50   ?? "—"} color="#7c3aed" />
          <StatCard label="10DMA"        value={stats?.dma10   ?? "—"} color="#ca8a04" />
          <StatCard label="Blue Bars"    value={stats?.blue    ?? "—"} color="#0891b2" />
          <StatCard label="Red Bars"     value={stats?.red     ?? "—"} color="#e11d48" />
          <StatCard label="HHPC"         value={stats?.hhpc    ?? "—"} color="#16a34a" />
          <StatCard label="LLPC"         value={stats?.llpc    ?? "—"} color="#f97316" />
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="mb-toolbar" aria-label="Chart toolbar">
        {/* Timeframe */}
        <div className="mb-group">
          <span className="mb-group-title">Timeframe</span>
          {["daily","weekly"].map(tf => (
            <button
              key={tf}
              className={`mb-btn${timeframe === tf ? " active primary" : ""}`}
              onClick={() => setTimeframe(tf)}
            >
              {tf === "daily" ? "Daily" : "Weekly"}
            </button>
          ))}
        </div>

        {/* Range */}
        <div className="mb-group">
          <span className="mb-group-title">Range</span>
          {RANGES.map(r => (
            <button
              key={r}
              className={`mb-btn${range === r ? " active primary" : ""}`}
              onClick={() => handleRange(r)}
            >
              {r}
            </button>
          ))}
        </div>

        {/* Navigate */}
        <div className="mb-group">
          <span className="mb-group-title">Navigate</span>
          <button className="mb-btn" onClick={() => chartActionsRef.current?.panLeft()}>Pan Left</button>
          <button className="mb-btn" onClick={() => chartActionsRef.current?.panRight()}>Pan Right</button>
          <button className="mb-btn" onClick={() => chartActionsRef.current?.goLatest()}>Latest</button>
          <button className="mb-btn" onClick={() => chartActionsRef.current?.fit()}>Fit</button>
        </div>

        {/* Zoom */}
        <div className="mb-group">
          <span className="mb-group-title">Zoom</span>
          <button className="mb-btn" onClick={() => chartActionsRef.current?.zoomIn()}>Zoom In</button>
          <button className="mb-btn" onClick={() => chartActionsRef.current?.zoomOut()}>Zoom Out</button>
          <select
            className="mb-select"
            value={barSpacing}
            onChange={e => handleBarSpacing(e.target.value)}
          >
            <option value={4}>Dense</option>
            <option value={7}>Normal</option>
            <option value={11}>Wide</option>
          </select>
        </div>

        {/* Display */}
        <div className="mb-group">
          <span className="mb-group-title">Display</span>
          <button 
            className="mb-btn" 
            onClick={() => setTheme(t => t === "dark" ? "light" : "dark")}
            title="Toggle Theme"
          >
            {theme === "dark" ? <Sun size={14} className="inline mr-1" /> : <Moon size={14} className="inline mr-1" />}
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          <button className={`mb-btn${showCrosshair ? " active" : ""}`} onClick={handleCrosshair}>Crosshair</button>
          <button className={`mb-btn${showGrid      ? " active" : ""}`} onClick={handleGrid}>Grid</button>
          <button className={`mb-btn${showPaneLabels? " active" : ""}`} onClick={() => setShowPaneLabels(v => !v)}>Pane Labels</button>
          <button className={`mb-btn${compact       ? " active" : ""}`} onClick={() => setCompact(v => !v)}>Compact</button>
          <button className="mb-btn" onClick={() => chartActionsRef.current?.downloadPng()}>PNG</button>
        </div>

        {/* Breadth */}
        <div className="mb-group" style={{ borderRight: 0 }}>
          <span className="mb-group-title">Breadth</span>
          <button className="mb-btn primary" onClick={handleShowAll}>Show All</button>
          <button className="mb-btn" onClick={handleHideAll}>Hide All</button>
          {INDICATORS.map(ind => (
            <button
              key={ind.key}
              className={`mb-btn${visibleSeries.has(ind.key) ? " active" : ""}`}
              onClick={() => handleToggleSeries(ind.key)}
              style={visibleSeries.has(ind.key) ? { borderColor: ind.color, color: ind.color, background: hexAlpha(ind.color, 0.08) } : {}}
            >
              {seriesLabel(ind.key)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Chart area ── */}
      <div className="mb-chart-wrap">
        {loading && (
          <div className="mb-loader">
            <div className="mb-spinner" />
            <span>Loading breadth data…</span>
          </div>
        )}
        {error && !loading && (
          <div className="mb-error">
            <span>{error}</span>
            <button className="mb-btn" onClick={fetchData}>Retry</button>
          </div>
        )}
        {!loading && !error && visibleSeries.size === 0 && breadthData && (
          <div className="mb-empty-note">
            All breadth panes are hidden. Use <strong>Show All</strong> or enable an individual breadth toggle to restore panes.
          </div>
        )}
        {!loading && !error && breadthData && (
          <BreadthChart
            breadthData={breadthData}
            visibleSeries={visibleSeries}
            timeframe={timeframe}
            range={range}
            barSpacing={barSpacing}
            showCrosshair={showCrosshair}
            showGrid={showGrid}
            showPaneLabels={showPaneLabels}
            compact={compact}
            indexLabel={indexLabel}
            theme={theme}
            onChartReady={(actions) => { chartActionsRef.current = actions; }}
          />
        )}
      </div>

      {/* ── Legend ── */}
      <div className="mb-legend">
        <LegendItem swatch="candle-up"   label={`${indexLabel} up candle`} />
        <LegendItem swatch="candle-down" label={`${indexLabel} down candle`} />
        {INDICATORS.map(ind => (
          <LegendItem key={ind.key} color={ind.color} label={ind.label} />
        ))}
      </div>
    </div>
    </DashboardLayout>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div className="mb-stat">
      <div className="mb-stat-label">{label}</div>
      <div className="mb-stat-value" style={color ? { color } : {}}>
        {value}
      </div>
    </div>
  );
}

function LegendItem({ swatch, color, label }) {
  return (
    <span className="mb-legend-item">
      <span
        className={`mb-swatch${swatch ? " mb-swatch--" + swatch : ""}`}
        style={color ? { background: color } : {}}
      />
      {label}
    </span>
  );
}

// ── Utilities ───────────────────────────────────────────────────────────────
function fmt(v) {
  if (v == null || isNaN(v)) return "—";
  return `${Number(v).toFixed(2)}%`;
}

function seriesLabel(key) {
  const map = {
    breadth_200dma_pct:   "200DMA",
    breadth_50dma_pct:    "50DMA",
    breadth_10dma_pct:    "10DMA",
    breadth_blue_bar_pct: "Blue Bars",
    breadth_red_bar_pct:  "Red Bars",
    breadth_hhpc_pct:     "HHPC",
    breadth_llpc_pct:     "LLPC",
  };
  return map[key] ?? key;
}

function hexAlpha(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
